import moment from "moment";
import "moment/locale/fr";
import "moment/locale/es";
import "moment/locale/en-gb";

import { ReportsEudrModel } from "@components/reports/eudr/reports-eudr.model";
import { RiskAssessmentTemplateResourceType } from "@components/shared/risk-assessment-templates/constants";
import { CommonConstants, TextConstants } from "@shared/constants";
import {
  ColorEnum,
  CustomFieldsResourceTypeEnum,
  CustomFieldsTypeEnum,
  LanguageEnum,
  ProcessStateEnum,
  UnitOfMeasurementCategoryTypeEnum,
  UnitOfMeasurementSystemNameEnum,
  UserRoleEnum,
} from "@shared/enums";

import { CommonUtils } from "./common.utils";

const getLanguageFromBrowserLocale = (): string => {
  const browserLocale = getBrowserLocale();

  return browserLocale
    .substring(0, browserLocale.indexOf("-") !== -1 ? browserLocale.indexOf("-") : undefined)
    .toLowerCase();
};

const getBrowserLocale = (): string => navigator.language || navigator["userLanguage"];

const getLocaleForAngularConfig = (): string => {
  let localeId = CommonConstants.DEFAULT_LANGUAGE_LOCALE_ID;
  const browserLanguage = LocalizationUtils.getLanguageFromBrowserLocale();

  //todo check for featureFlagLanguages here too.
  if (CommonConstants.AVAILABLE_LANGUAGE_CODES.includes(browserLanguage as LanguageEnum)) {
    localeId = LocalizationUtils.getBrowserLocale();
  }

  return localeId;
};

const getLocaleForMoment = (): string => {
  const localeForAngular = getLocaleForAngularConfig().toLowerCase();

  if (localeForAngular === "en-us") {
    return "en";
  } else if (localeForAngular.startsWith("es")) {
    return "es";
  } else if (localeForAngular.startsWith("fr")) {
    return "fr";
  } else {
    return "en-gb";
  }
};

const getLocaleDatepickerFormat = (): string => {
  return moment.localeData(getLocaleForMoment()).longDateFormat("L");
};

const getLocaleNumberSeparator = (separatorType: "decimal" | "group") => {
  const numberWithGroupAndDecimalSeparator = 1000.1;

  return Intl.NumberFormat(getLocaleForAngularConfig())
    .formatToParts(numberWithGroupAndDecimalSeparator)
    .find((part) => part.type === separatorType).value;
};

const getCleanNumber = (value: number | string): string => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "number") {
    return value.toString();
  }

  return value.replaceAll(getLocaleNumberSeparator("group"), "");
};

const getEntityType = (
  type: string,
  isReturnPlural: boolean = false,
  returnFormat: "capitaliseFirstLetter" | "lowercase" = "capitaliseFirstLetter",
): string => {
  if (!type) {
    return null;
  }
  const formattedType = type.toLowerCase().replaceAll("_", " ").replaceAll("-", " ");
  let result: string;

  switch (formattedType) {
    case "locations":
    case "location":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.locations:Locations`
        : $localize`:@@shared.entitytype.location:Location`;
      break;
    case "connections":
    case "connection":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.connections:Connections`
        : $localize`:@@shared.entitytype.connection:Connection`;
      break;
    case "certificates":
    case "certificate":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.certificates:Certificates`
        : $localize`:@@shared.entitytype.certificate:Certificate`;
      break;
    case "documents":
    case "document":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.documents:Documents`
        : $localize`:@@shared.entitytype.document:Document`;
      break;
    case "items":
    case "item":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.items:Items`
        : $localize`:@@shared.entitytype.item:Item`;
      break;
    case "deliveries":
    case "delivery":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.deliveries:Deliveries`
        : $localize`:@@shared.entitytype.delivery:Delivery`;
      break;
    case "processes":
    case "process":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.processes:Processes`
        : $localize`:@@shared.entitytype.processes:Process`;
      break;
    case "products":
    case "product":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.products:Products`
        : $localize`:@@shared.entitytype.product:Product`;
      break;
    case "supply chains":
    case "supply chain":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.supplychains:Supply chains`
        : $localize`:@@shared.entitytype.supplychain:Supply chain`;
      break;
    case "organisations":
    case "organisation":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.organisations:Organisations`
        : $localize`:@@shared.entitytype.organisation:Organisation`;
      break;
    case "materials":
    case "material":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.materials:Materials`
        : $localize`:@@shared.entitytype.material:Material`;
      break;
    case "countries":
    case "country":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.countries:Countries`
        : $localize`:@@shared.entitytype.country:Country`;
      break;
    case "location types":
    case "location type":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.locationtypes:Location types`
        : $localize`:@@shared.entitytype.locationtype:Location type`;
      break;
    case "document types":
    case "document type":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.documenttypes:Document types`
        : $localize`:@@shared.entitytype.documenttype:Document type`;
      break;
    case "process types":
    case "process type":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.processtypes:Process types`
        : $localize`:@@shared.entitytype.processtype:Process type`;
      break;
    case "dds references":
    case "dds reference":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.ddsreferences:DDS references`
        : $localize`:@@shared.entitytype.ddsreference:DDS reference`;
      break;
    case "individuals":
    case "individual":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.individuals:Individuals`
        : $localize`:@@shared.entitytype.individual:Individual`;
      break;
    case "purchase orders":
    case "purchase order":
      result = isReturnPlural
        ? $localize`:@@shared.entitytype.purchaseorders:Purchase orders`
        : $localize`:@@shared.entitytype.purchaseorder:Purchase order`;
      break;
    default:
      result = type;
  }

  switch (returnFormat) {
    case "capitaliseFirstLetter":
      return CommonUtils.capitaliseFirstLetter(result);
    case "lowercase":
      return result.toLowerCase();
  }
};

const getCustomFieldResourceType = (type: CustomFieldsResourceTypeEnum): string => {
  switch (type) {
    case CustomFieldsResourceTypeEnum.ORGANISATION:
      return $localize`:@@shared.customfield.resourcetype.organisation:My organisation`;
    case CustomFieldsResourceTypeEnum.CONNECTION:
      return $localize`:@@shared.customfield.resourcetype.connection:Organisations`;
    case CustomFieldsResourceTypeEnum.LOCATION:
      return $localize`:@@shared.customfield.resourcetype.location:Locations`;
    case CustomFieldsResourceTypeEnum.ITEM:
      return $localize`:@@shared.customfield.resourcetype.item:Items`;
    case CustomFieldsResourceTypeEnum.DELIVERY:
      return $localize`:@@shared.customfield.resourcetype.delivery:Deliveries`;
    case CustomFieldsResourceTypeEnum.PROCESS:
      return $localize`:@@shared.customfield.resourcetype.process:Processes`;
    case CustomFieldsResourceTypeEnum.PRODUCT:
      return $localize`:@@shared.customfield.resourcetype.product:Products`;
    case CustomFieldsResourceTypeEnum.MATERIAL:
      return $localize`:@@shared.customfield.resourcetype.material:Materials`;
    case CustomFieldsResourceTypeEnum.CERTIFICATE:
      return $localize`:@@shared.customfield.resourcetype.certificate:Certificates`;
    default:
      return CommonUtils.enumToText(type);
  }
};

const getCustomFieldType = (type: CustomFieldsTypeEnum): string => {
  switch (type) {
    case CustomFieldsTypeEnum.TEXT:
      return $localize`:@@shared.customfield.type.text:Text`;
    default:
      return CommonUtils.enumToText(type);
  }
};

const getUnitOfMeasurementSystemName = (name: UnitOfMeasurementSystemNameEnum): string => {
  switch (name) {
    case UnitOfMeasurementSystemNameEnum.CUBIC_METERS:
      return $localize`:@@shared.unitofmeasurement.systemname.cubicmeters:Cubic meters`;
    case UnitOfMeasurementSystemNameEnum.KILOGRAMS:
      return $localize`:@@shared.unitofmeasurement.systemname.kilograms:Kilograms`;
    case UnitOfMeasurementSystemNameEnum.SQUARE_METERS:
      return $localize`:@@shared.unitofmeasurement.systemname.squaremeters:Square meters`;
    case UnitOfMeasurementSystemNameEnum.METERS:
      return $localize`:@@shared.unitofmeasurement.systemname.meters:Meters`;
    case UnitOfMeasurementSystemNameEnum.UNITS:
      return $localize`:@@shared.unitofmeasurement.systemname.units:Units`;
    default:
      return CommonUtils.enumToText(name);
  }
};

const getUnitOfMeasurementCategory = (category: UnitOfMeasurementCategoryTypeEnum): string => {
  switch (category) {
    case UnitOfMeasurementCategoryTypeEnum.VOLUME:
      return $localize`:@@shared.unitofmeasurement.category.volume:Volume`;
    case UnitOfMeasurementCategoryTypeEnum.MASS:
      return $localize`:@@shared.unitofmeasurement.category.mass:Mass`;
    case UnitOfMeasurementCategoryTypeEnum.AREA:
      return $localize`:@@shared.unitofmeasurement.category.area:Area`;
    case UnitOfMeasurementCategoryTypeEnum.LENGTH:
      return $localize`:@@shared.unitofmeasurement.category.length:Length`;
    case UnitOfMeasurementCategoryTypeEnum.UNITS:
      return $localize`:@@shared.unitofmeasurement.category.units:Units`;
    default:
      return CommonUtils.enumToText(category);
  }
};

const getColor = (color: ColorEnum): string => {
  switch (color) {
    case ColorEnum.LIGHT_GREY:
      return $localize`:@@shared.color.lightgrey:Light grey`;
    case ColorEnum.GREY:
      return $localize`:@@shared.color.grey:Grey`;
    case ColorEnum.BLUE:
      return $localize`:@@shared.color.blue:Blue`;
    case ColorEnum.PURPLE:
      return $localize`:@@shared.color.purple:Purple`;
    case ColorEnum.GREEN:
      return $localize`:@@shared.color.green:Green`;
    case ColorEnum.YELLOW:
      return $localize`:@@shared.color.yellow:Yellow`;
    case ColorEnum.ORANGE:
      return $localize`:@@shared.color.orange:Orange`;
    case ColorEnum.RED:
      return $localize`:@@shared.color.red:Red`;
    default:
      return CommonUtils.enumToText(color);
  }
};

const getUserRole = (userRole: UserRoleEnum): string => {
  switch (userRole) {
    case UserRoleEnum.ACCOUNT_OWNER:
      return $localize`:@@shared.role.accountowner:Account owner`;
    case UserRoleEnum.ADMIN:
      return $localize`:@@shared.role.admin:Admin`;
    case UserRoleEnum.CONTRIBUTOR:
      return $localize`:@@shared.role.contributor:Contributor`;
    case UserRoleEnum.REVIEWER:
      return $localize`:@@shared.role.reviewer:Reviewer`;
    default:
      return CommonUtils.enumToText(userRole);
  }
};

const getProcessStateLabel = (processState: ProcessStateEnum): string => {
  switch (processState) {
    case ProcessStateEnum.INPUT_OUTPUT:
      return $localize`:@@process.state.input_output:Input & Output`;
    case ProcessStateEnum.OUTPUT_ONLY:
      return $localize`:@@process.state.output_only:Output Only`;
    case ProcessStateEnum.CLOSED:
      return $localize`:@@process.state.closed:Closed`;
    default:
      return CommonUtils.enumToText(processState);
  }
};

const getStatus = (status: string): string => {
  if (!status) {
    return null;
  }
  const statusLowerCase = status.toLowerCase().replaceAll("_", " ");

  switch (statusLowerCase) {
    case "active":
      return $localize`:@@shared.status.active:Active`;
    case "deactivated":
      return $localize`:@@shared.status.deactivated:Deactivated`;
    case "pending":
      return $localize`:@@shared.status.pending:Pending`;
    case "accepted":
      return $localize`:@@shared.status.accepted:Accepted`;
    case "rejected":
      return $localize`:@@shared.status.rejected:Rejected`;
    case "enabled":
      return $localize`:@@shared.status.enabled:Enabled`;
    case "disabled":
      return $localize`:@@shared.status.disabled:Disabled`;
    case "sent":
      return $localize`:@@shared.status.sent:Sent`;
    case "delivered":
      return $localize`:@@shared.status.delivered:Delivered`;
    case "new":
      return $localize`:@@shared.status.new:New`;
    case "existing":
      return $localize`:@@shared.status.existing:Existing`;
    case "archived":
      return $localize`:@@shared.status.archived:Archived`;
    case "deleted":
      return $localize`:@@shared.status.deleted:Deleted`;
    case "removed":
      return $localize`:@@shared.status.removed:Removed`;
    case "viewed":
      return $localize`:@@shared.status.viewed:Viewed`;
    case "available":
      return $localize`:@@shared.status.available:Available`;
    case "unavailable":
      return $localize`:@@shared.status.unavailable:Unavailable`;
    case "connected":
      return $localize`:@@shared.status.connected:Connected`;
    case "disconnected":
      return $localize`:@@shared.status.disconnected:Disconnected`;
    case "completed":
      return $localize`:@@shared.status.completed:Completed`;
    case "in progress":
      return $localize`:@@shared.status.inprogress:In progress`;
    case "not applicable":
      return $localize`:@@shared.status.notapplicable:Not applicable`;
    case "invalidated":
      return $localize`:@@shared.status.invalidated:Invalidated`;
    case "under review":
      return $localize`:@@shared.status.underreview:Under review`;
    case "approved":
      return $localize`:@@shared.status.approved:Approved`;
    case "open":
      return $localize`:@@shared.status.open:Open`;
    case "cancelled":
      return $localize`:@@shared.status.cancelled:Cancelled`;
    default:
      return CommonUtils.capitaliseFirstLetter(status);
  }
};

const getEudrActivityType = (activity: ReportsEudrModel.ActivityEnum): string => {
  switch (activity) {
    case ReportsEudrModel.ActivityEnum.DOMESTIC:
      return $localize`:@@eudr.activity.domestic:Domestic`;
    case ReportsEudrModel.ActivityEnum.IMPORT:
      return $localize`:@@eudr.activity.import:Import`;
    case ReportsEudrModel.ActivityEnum.EXPORT:
      return $localize`:@@eudr.activity.export:Export`;
    default:
      return CommonUtils.enumToText(activity);
  }
};

const getRelatedRiskAssessmentRecordType: Record<RiskAssessmentTemplateResourceType, string> = {
  [RiskAssessmentTemplateResourceType.COUNTRY]: $localize`:@@relatedriskassessments.record.countries:Country(ies)`,
  [RiskAssessmentTemplateResourceType.DELIVERY]: $localize`:@@relatedriskassessments.record.deliveries:Delivery(ies)`,
  [RiskAssessmentTemplateResourceType.LOCATION]: $localize`:@@relatedriskassessments.record.locations:Location(s)`,
  [RiskAssessmentTemplateResourceType.MATERIAL]: $localize`:@@relatedriskassessments.record.materials:Material(s)`,
  [RiskAssessmentTemplateResourceType.ORGANISATION]: $localize`:@@relatedriskassessments.record.organisations:Organisation(s)`,
  [RiskAssessmentTemplateResourceType.PRODUCT]: $localize`:@@relatedriskassessments.record.products:Product(s)`,
};

const getRiskAssessmentTemplateResourceType: Record<RiskAssessmentTemplateResourceType, string> = {
  [RiskAssessmentTemplateResourceType.COUNTRY]: TextConstants.COUNTRY,
  [RiskAssessmentTemplateResourceType.DELIVERY]: TextConstants.DELIVERY,
  [RiskAssessmentTemplateResourceType.LOCATION]: TextConstants.LOCATION,
  [RiskAssessmentTemplateResourceType.MATERIAL]: TextConstants.MATERIAL,
  [RiskAssessmentTemplateResourceType.ORGANISATION]: TextConstants.ORGANISATION,
  [RiskAssessmentTemplateResourceType.PRODUCT]: TextConstants.PRODUCT,
};

export const LocalizationUtils = {
  getLanguageFromBrowserLocale,
  getBrowserLocale,
  getLocaleForAngularConfig,
  getLocaleForMoment,
  getLocaleDatepickerFormat,
  getLocaleNumberSeparator,
  getCleanNumber,
  getEntityType,
  getCustomFieldResourceType,
  getCustomFieldType,
  getUnitOfMeasurementSystemName,
  getUnitOfMeasurementCategory,
  getColor,
  getUserRole,
  getStatus,
  getEudrActivityType,
  getProcessStateLabel,
  getRelatedRiskAssessmentRecordType,
  getRiskAssessmentTemplateResourceType,
};
